/**
 * Expunerile: planograme, casete și vitrine agreate la puncte de lucru.
 *
 * Agentul din teren gândește „sunt la Ana Maria, ce avem aici?", nu în campanii — de aceea
 * ecranul pleacă de la căutarea după punct de lucru, nu de la containerul în care stau rândurile.
 *
 * Toți văd tot, inclusiv costul: un agent care intră într-o farmacie care nu e a lui trebuie să
 * vadă ce e agreat acolo (Alexandru, 23.09.2026).
 */
import { useState } from 'react'
import type { PunctLucru } from '../../components/marketing/PunctLucruSelect'
import { FarmaciaSiPunctul } from '../../components/marketing/FarmaciaSiPunctul'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { DocumenteContainer } from '../../components/marketing/DocumenteContainer'

interface Expunere {
  id: number
  denumire: string
  punctDeLucru: string | null
  instrument: { id: number; denumire: string } | null
  dataStart: string | null
  dataFinal: string | null
  costLunar: number
  moneda: 'LEI' | 'EUR'
  curs: number | null
  luni: number
  total: number
  poze: number
  zileRamase: number | null
}

interface RandAn {
  an: number
  luni: number
  suma: number
}

/**
 * Instrumentul, asa cum vine pe ruta de ALEGERE: doar ce trebuie ca sa-l pui intr-o optiune.
 *
 * Fara `activ`, si asta nu e o scapare: ruta intoarce numai instrumente active, deci un camp pe
 * care sa-l filtrezi aici ar fi o a doua sursa de adevar pentru aceeasi regula. Prima incercare
 * a pastrat tipul comun si filtrul din pagina — iar filtrul a taiat tot, fiindca `activ` era
 * `undefined`. Tipul ingust face greseala imposibila, nu doar improbabila.
 */
interface InstrumentDeAles {
  id: number
  denumire: string
}

const lei = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const zi = (s: string | null) => (s ? new Date(s).toLocaleDateString('ro-RO') : '—')

/** `2026-09-01` — intai ale lunii curente, cu care pleaca formularul. */
function intaiAleLunii(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

/**
 * Ultima zi a lunii in care se termina `luni` luni incepute la `start`.
 *
 * Inversul lui `luniAcoperite` de pe server, care numara luna inceputa ca intreaga: 01.09 plus
 * 6 luni se termina pe 28.02, fiindca septembrie–februarie SUNT sase luni.
 *
 * Nu se pierde nimic mergand pana la capatul lunii: o intelegere incheiata pe 20.12 costa la fel
 * ca una incheiata pe 31.12, tocmai fiindca luna inceputa se plateste intreaga.
 */
function finalDupaLuni(start: string, luni: number): string {
  if (!start || !(luni > 0)) return ''
  const d = new Date(start)
  if (Number.isNaN(d.getTime())) return ''
  // Ziua 0 a lunii urmatoare = ultima zi a lunii cerute.
  const f = new Date(d.getFullYear(), d.getMonth() + luni, 0)
  return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`
}

export function ExpuneriPage() {
  const qc = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const poateScrie = permissions.includes('marketing.sampling:write')
  const poateSterge = permissions.includes('marketing.sampling:delete')

  const raport = useQuery<{ raport: { ani: RandAn[]; total: number } }>({
    queryKey: ['expuneri', 'raport-pe-an'],
    queryFn: async () =>
      (
        await api.get<{ raport: { ani: RandAn[]; total: number } }>(
          '/marketing/expuneri/raport-pe-an',
        )
      ).data,
  })

  const [cautare, setCautare] = useState('')
  const [doarActive, setDoarActive] = useState(true)
  const [formularDeschis, setFormularDeschis] = useState(false)
  const [pozeLa, setPozeLa] = useState<Expunere | null>(null)
  const [deSters, setDeSters] = useState<Expunere | null>(null)

  /*
    Stergerea foloseste ruta actiunilor, care e doar a adminului si refuza orice actiune care a
    produs documente. O expunere n-are cum sa produca vreunul, deci aici opreste doar dreptul.

    E nevoie de ea: o expunere adaugata pe punctul gresit n-ar avea altfel cum sa dispara, iar
    lista ar purta greseala la nesfarsit.
  */
  const sterge = useMutation({
    mutationFn: async (id: number) => api.delete(`/marketing/sampling/actiuni/${id}`),
    onSuccess: () => {
      setDeSters(null)
      void qc.invalidateQueries({ queryKey: ['expuneri'] })
      toast.success('Expunere ștearsă.')
    },
    onError: (err: unknown) => {
      const mesaj = (err as { response?: { data?: { error?: { message?: string } } } })?.response
        ?.data?.error?.message
      toast.error(mesaj ?? 'Nu s-a putut șterge.')
    },
  })

  const lista = useQuery<{ expuneri: Expunere[] }>({
    queryKey: ['expuneri', cautare, doarActive],
    queryFn: async () =>
      (
        await api.get<{ expuneri: Expunere[] }>('/marketing/expuneri', {
          params: { q: cautare || undefined, doarActive: doarActive ? 'true' : '' },
        })
      ).data,
  })

  const expuneri = lista.data?.expuneri ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Expuneri</h1>
        {poateScrie && <Button onClick={() => setFormularDeschis(true)}>+ Expunere nouă</Button>}
      </div>

      {/*
        Anul nu vine din containerul expunerilor — acela e unul singur, fără an în nume, tocmai
        ca să nu pretindă unul pe care nu-l poate ține. Vine din perioada fiecărei expuneri,
        tăiată pe luni. De-asta o înțelegere care trece anul apare pe amândoi anii, cu partea ei.
      */}
      {(raport.data?.raport.ani.length ?? 0) > 0 && (
        <Card description="Cât costă expunerile în fiecare an. O înțelegere care trece anul apare pe amândoi, cu partea care cade în fiecare — tariful fiind lunar, tăierea e exactă.">
          <ul className="divide-y divide-b4l-border">
            {raport.data?.raport.ani.map((a) => (
              <li key={a.an} className="flex items-baseline gap-3 py-1.5 text-sm">
                <span className="w-16 font-medium tabular-nums text-b4l-text">{a.an}</span>
                <span className="text-xs text-b4l-text-muted">
                  {a.luni} {a.luni === 1 ? 'lună' : 'luni'}
                </span>
                <span className="ml-auto tabular-nums text-b4l-text">{lei(a.suma)} lei</span>
              </li>
            ))}
            <li className="flex items-baseline gap-3 pt-2 text-sm font-semibold">
              <span className="w-16">Total</span>
              <span className="ml-auto tabular-nums">
                {lei(raport.data?.raport.total ?? 0)} lei
              </span>
            </li>
          </ul>
        </Card>
      )}

      <Card description="Ce am agreat la fiecare punct de lucru — planogramă, casetă, vitrină — pe ce perioadă și cu ce cost. Poza e dovada că s-a implementat.">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            value={cautare}
            onChange={(e) => setCautare(e.target.value)}
            placeholder="Caută după client sau punct de lucru"
            className="min-w-0 flex-1 rounded border border-b4l-border px-2 py-1.5 text-sm"
          />
          <label className="flex items-center gap-1.5 text-xs text-b4l-text-muted">
            <input
              type="checkbox"
              checked={doarActive}
              onChange={(e) => setDoarActive(e.target.checked)}
            />
            Doar cele în curs
          </label>
        </div>

        {lista.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {!lista.isLoading && expuneri.length === 0 && (
          <p className="text-sm text-b4l-text-muted">
            {cautare ? 'Nimic găsit.' : 'Nicio expunere încă.'}
          </p>
        )}

        <ul className="space-y-2">
          {expuneri.map((e) => (
            <li key={e.id} className="rounded-lg border border-b4l-border p-3">
              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-b4l-text">{e.punctDeLucru}</p>
                  <p className="mt-0.5 text-xs text-b4l-text-muted">
                    {e.instrument?.denumire ?? 'fără instrument'} · {zi(e.dataStart)} –{' '}
                    {zi(e.dataFinal)}
                  </p>
                </div>
                <Expirare zile={e.zileRamase} />
              </div>

              {/*
                Amândouă cifrele, mereu. Tariful e ce s-a negociat, totalul e ce costă în fapt —
                iar fără al doilea nimeni nu-și dă seama cât a angajat până la urmă.
              */}
              <p className="mt-2 text-sm tabular-nums text-b4l-text">
                {lei(e.costLunar)} {e.moneda === 'EUR' ? 'EUR' : 'lei'}/lună
                <span className="text-b4l-text-muted">
                  {' · '}
                  {lei(e.total)} lei total ({e.luni} {e.luni === 1 ? 'lună' : 'luni'})
                  {/*
                    La euro se arata si cursul retinut. Fara el, cineva care aduna in cap 300 × 6
                    si vede alt numar in lei n-ar avea de unde sti de ce.
                  */}
                  {e.moneda === 'EUR' && e.curs ? `, la cursul ${e.curs.toFixed(4)}` : ''}
                </span>
              </p>

              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPozeLa(e)}
                  className="text-sm text-b4l-blue-dark hover:underline"
                >
                  {e.poze > 0 ? `Documente (${e.poze})` : 'Adaugă documente'}
                </button>
                {poateSterge && (
                  <button
                    type="button"
                    onClick={() => setDeSters(e)}
                    className="ml-auto text-sm text-b4l-red-dark hover:underline"
                  >
                    Șterge
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      {formularDeschis && (
        <FormularExpunere
          onInchide={() => setFormularDeschis(false)}
          onSalvat={() => {
            setFormularDeschis(false)
            void qc.invalidateQueries({ queryKey: ['expuneri'] })
          }}
        />
      )}

      {deSters && (
        <Modal open title="Ștergi expunerea?" onClose={() => setDeSters(null)}>
          <div className="space-y-3">
            <p className="text-sm text-b4l-text">
              <strong>{deSters.punctDeLucru}</strong> · {deSters.instrument?.denumire} ·{' '}
              {zi(deSters.dataStart)} – {zi(deSters.dataFinal)}
            </p>
            <p className="text-sm text-b4l-text-muted">
              Dispare și costul angajat de {lei(deSters.total)} lei. Pozele urcate se pierd odată cu
              ea.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeSters(null)}>
                Renunță
              </Button>
              <Button
                variant="danger"
                loading={sterge.isPending}
                onClick={() => sterge.mutate(deSters.id)}
              >
                Șterge
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {pozeLa && (
        <Modal
          open
          title={`Documente — ${pozeLa.punctDeLucru ?? ''}`}
          onClose={() => {
            setPozeLa(null)
            void qc.invalidateQueries({ queryKey: ['expuneri'] })
          }}
        >
          <DocumenteContainer tip="actiune" parinteId={pozeLa.id} canWrite={poateScrie} />
        </Modal>
      )}
    </div>
  )
}

/** Cât mai ține. Culoarea spune cât de aproape e finalul, fără să citești data. */
function Expirare({ zile }: { zile: number | null }) {
  if (zile === null) return null
  if (zile < 0) {
    return (
      <span className="rounded-full bg-b4l-bg px-2 py-0.5 text-xs text-b4l-text-muted">
        expirată
      </span>
    )
  }
  const culoare =
    zile <= 7
      ? 'bg-b4l-red-light text-b4l-red-dark'
      : zile <= 21
        ? 'bg-b4l-amber-light text-b4l-text'
        : 'bg-b4l-bg text-b4l-text-muted'
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ${culoare}`}>
      {zile === 0 ? 'expiră azi' : `${zile} zile`}
    </span>
  )
}

function FormularExpunere({
  onInchide,
  onSalvat,
}: {
  onInchide: () => void
  onSalvat: () => void
}) {
  const [clientId, setClientId] = useState<number | null>(null)
  const [punct, setPunct] = useState<PunctLucru | null>(null)
  const branchId = punct?.id ?? null

  const [instrumentId, setInstrumentId] = useState('')
  /*
    Perioada se cere ca START + NUMAR DE LUNI, nu ca doua date.
    Asa se si negociaza — „sase luni", nu „pana pe 28 februarie" — iar data de final o socoteste
    programul, nu omul in cap. Startul vine completat cu intai ale lunii curente, cazul obisnuit.
  */
  const [dataStart, setDataStart] = useState(intaiAleLunii())
  const [luni, setLuni] = useState('')
  const dataFinal = finalDupaLuni(dataStart, Number(luni))
  const [costLunar, setCostLunar] = useState('')
  const [moneda, setMoneda] = useState<'LEI' | 'EUR'>('LEI')
  const [observatii, setObservatii] = useState('')

  /*
    `/alegere`, nu `/` — aceea e ruta de administrare a nomenclatorului si cere permisiune de
    Setari, pe care o au doar adminii. Lista de aici se deschide tuturor celor care pot deschide
    ecranul, si arata doar instrumentele active.
  */
  const instrumente = useQuery<{ instrumente: InstrumentDeAles[] }>({
    queryKey: ['instrumente-promovare', 'alegere'],
    queryFn: async () =>
      (await api.get<{ instrumente: InstrumentDeAles[] }>('/marketing/instrumente/alegere')).data,
  })

  const salveaza = useMutation({
    mutationFn: async () =>
      (
        await api.post('/marketing/expuneri', {
          branchId,
          instrumentId: Number(instrumentId),
          dataStart,
          dataFinal,
          costLunar: Number(costLunar),
          moneda,
          observatii: observatii.trim() || undefined,
        })
      ).data,
    onSuccess: () => {
      toast.success('Expunere adăugată.')
      onSalvat()
    },
    onError: (err: unknown) => {
      const mesaj = (err as { response?: { data?: { error?: { message?: string } } } })?.response
        ?.data?.error?.message
      toast.error(mesaj ?? 'Nu s-a putut salva.')
    },
  })

  const complet = branchId && instrumentId && dataStart && dataFinal && Number(costLunar) > 0
  /* Filtrarea o face serverul. Vezi `InstrumentDeAles`. */
  const active = instrumente.data?.instrumente ?? []

  return (
    <Modal open title="Expunere nouă" onClose={onInchide}>
      <div className="space-y-3">
        {/*
          Intai farmacia, apoi punctul ei de lucru. O cautare directa printre cele 15.000 de
          puncte scoate zeci de randuri aproape identice — „Dr.Max Iasi 50", „Dr.Max Iasi 51" —
          si nu se poate parcurge (Alexandru, 23.09.2026). Cu lantul ales, lista se scurteaza la
          punctele lui.

          Sincronizarea merge in ambele sensuri, ca la wizardul de sampling: punctul stie de ce
          lant tine si il completeaza, iar schimbarea lantului scoate un punct care nu mai e al
          lui — altfel ar ramane perechi care nu inseamna nimic.
        */}
        <FarmaciaSiPunctul
          clientId={clientId}
          punct={punct}
          onClient={setClientId}
          onPunct={setPunct}
          etichetaClient="Farmacia / lanțul"
          ajutorPunct="Un punct care lipsește se adaugă cu „+ Punct nou” și pleacă în ExpertAccounts."
        />

        <label className="block text-xs text-b4l-text-muted">
          Ce se expune
          <select
            value={instrumentId}
            onChange={(e) => setInstrumentId(e.target.value)}
            className="mt-1 w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
          >
            <option value="">— alege —</option>
            {active.map((i) => (
              <option key={i.id} value={i.id}>
                {i.denumire}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-wrap gap-3">
          <label className="block flex-1 text-xs text-b4l-text-muted">
            De la
            <input
              type="date"
              value={dataStart}
              onChange={(e) => setDataStart(e.target.value)}
              className="mt-1 w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block w-32 text-xs text-b4l-text-muted">
            Număr de luni
            <input
              type="number"
              min={1}
              max={120}
              value={luni}
              onChange={(e) => setLuni(e.target.value)}
              className="mt-1 w-full rounded border border-b4l-border px-2 py-1.5 text-right text-sm tabular-nums"
            />
          </label>
        </div>
        {dataFinal && (
          <p className="-mt-1 text-xs text-b4l-text-muted">
            până la <strong className="text-b4l-text">{zi(dataFinal)}</strong>
          </p>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <label className="block flex-1 text-xs text-b4l-text-muted">
            Cost pe lună
            <input
              type="number"
              min={0}
              step="0.01"
              value={costLunar}
              onChange={(e) => setCostLunar(e.target.value)}
              className="mt-1 w-full rounded border border-b4l-border px-2 py-1.5 text-right text-sm tabular-nums"
            />
          </label>
          <label className="block text-xs text-b4l-text-muted">
            Moneda
            <select
              value={moneda}
              onChange={(e) => setMoneda(e.target.value as 'LEI' | 'EUR')}
              className="mt-1 rounded border border-b4l-border px-2 py-1.5 text-sm"
            >
              <option value="LEI">lei</option>
              <option value="EUR">EUR</option>
            </select>
          </label>
        </div>

        {/*
          Totalul se arată ÎNAINTE de salvare. Cine scrie 1.200 pe lună vede pe loc că înțelegerea
          costă 4.800 — altfel cifra mare apare abia după, când e deja scrisă în buget.
        */}
        <Previzualizare luni={Number(luni)} costLunar={costLunar} moneda={moneda} />

        <label className="block text-xs text-b4l-text-muted">
          Ce s-a agreat (opțional)
          <textarea
            value={observatii}
            onChange={(e) => setObservatii(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
          />
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onInchide}>
            Renunță
          </Button>
          <Button
            disabled={!complet || salveaza.isPending}
            loading={salveaza.isPending}
            onClick={() => salveaza.mutate()}
          >
            Salvează
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * Cât costă înțelegerea, înainte de salvare.
 *
 * Lunile NU se mai socotesc aici din cele două date: sunt chiar ce a scris omul. O a doua
 * socoteală a aceluiași lucru s-ar fi despărțit de prima la primul caz ciudat.
 */
function Previzualizare({
  luni,
  costLunar,
  moneda,
}: {
  luni: number
  costLunar: string
  moneda: 'LEI' | 'EUR'
}) {
  const tarif = Number(costLunar)
  if (!(luni > 0) || !(tarif > 0)) return null
  const unitate = moneda === 'EUR' ? 'EUR' : 'lei'
  return (
    <p className="rounded bg-b4l-bg-soft px-2 py-1.5 text-sm tabular-nums text-b4l-text">
      {luni} {luni === 1 ? 'lună' : 'luni'} × {lei(tarif)} ={' '}
      <strong>
        {lei(tarif * luni)} {unitate}
      </strong>
      {/*
        La euro, suma in lei nu se arata aici: cursul il stie serverul, iar o cifra socotita in
        ecran cu alt curs ar fi o a doua socoteala care se desparte de prima.
      */}
      {moneda === 'EUR' && (
        <span className="text-b4l-text-muted"> · în lei, la cursul BNR de azi</span>
      )}
    </p>
  )
}
